/**
 * What a part's code says about it — series, size, finish, cover line, colour —
 * so sales can be grouped by attribute (Trends) and parts named in plain
 * language (web lib/skuNames.ts). Names come from the Demand mockup; anything
 * unknown stays as the raw code, so nothing is invented.
 */
export const SERIES: Record<string, string> = {"AAT":"Rodeo","AATF":"Newport","ALTO":"Venture","MARE":"Grove","STAF":"Madera","SOLA":"Solana","SMPT":"Pagoda","SMPTF":"Pagoda","SMCF":"Shade Legacy","SMPF":"Shade Legacy","GSCU":"Golden State","GSCUF":"Sun Master","GSPT":"Pacific Trail","LUNA":"Terrace","SDAU":"Sunset","CALI":"Cali","BA":"Bayside","PTS":"Parkside","SUPF":"New Series","ES":"Tahoe"};
export const SIZE: Record<string, string> = {"118":"11 ft round","604":"6 ft square","758":"7.5 ft round","908":"9 ft round"};
export const FINISH: Record<string, string> = {"117":"Bronze","170":"Matted White","302":"Stone Black / Smoke","705":"Matted Black","724":"Driftwood Woodgrain","002":"Silver Anodized"};
export const FABRIC: Record<string, string> = {"F67":"Solstice F67 Champagne","F71":"Solstice F71 Teak","FD12":"Solstice FD12 Terrace Adobe","FD10":"Solstice FD10 Terrace Sequoia","F03":"Solstice F03 Royal Blue","F08":"Solstice F08 Hunter Green","F09":"Solstice F09 Navy Blue","F13":"Solstice F13 Red","F22":"Solstice F22 Antique Beige","F25":"Solstice F25 Lemon","F27":"Solstice F27 Sunset","F32":"Solstice F32 Black","F55":"Solstice F55 Kiwi","F69":"Solstice F69 Terracotta","SA08":"Pacifica Marine SA08 Black","SA26":"Pacifica Marine SA26","SA39":"Pacifica Marine SA39 Navy Blue","SA61":"Pacifica Marine SA61","SA03":"Pacifica Marine SA03","SA04":"Pacifica Marine SA04 Natural","SA36":"Pacifica Marine SA36","SA46":"Pacifica Marine SA46","SA53":"Pacifica Marine SA53","SA01":"Pacifica Marine SA01","F96":"Solstice F96","F04":"Solstice F04 White"};
export const SUNBRELLA: Record<string, string> = {"5425":"Cocoa","5432":"Bay Brown"};

/** The ways a category's sales can be grouped on Trends. */
export const TREND_DIMENSIONS: Record<string, Array<{ key: string; label: string }>> = {
  Frame: [
    { key: "finish", label: "Finish" },
    { key: "series", label: "Series" },
    { key: "size", label: "Size" },
  ],
  Cover: [
    { key: "colour", label: "Colour / fabric" },
    { key: "line", label: "Cover line" },
    { key: "size", label: "Size" },
    { key: "vent", label: "Wind vent" },
  ],
  Base: [{ key: "family", label: "Base family" }],
  Fabric: [{ key: "fabric", label: "Fabric" }],
  Trim: [
    { key: "kind", label: "Trim type" },
    { key: "colour", label: "Colour" },
  ],
  ProtectiveCover: [{ key: "part", label: "Part" }],
};

const sizeName = (code: string) => SIZE[code] ?? code;

/**
 * Attribute values for one part, keyed by dimension. A dimension the code
 * doesn't carry is absent (e.g. MARE908 has no finish), and the part then
 * falls under "Not in code" for that dimension.
 */
export function skuAttributes(sku: string, itemType: string): Record<string, string> {
  if (itemType === "Frame") {
    // ALTO908-117 · AATF118AH-002 · LUNA906B-DT-174 · SMPTF852-174-PD · MARE908
    const m = sku.match(/^([A-Z]+)(\d{3,5})[A-Z]*(?:-([A-Z]{1,3}))?(?:-(\d{3}))?(?:-[A-Z]+)?$/);
    if (!m) return {};
    const out: Record<string, string> = {
      series: SERIES[m[1]] ? `${SERIES[m[1]]} (${m[1]})` : m[1],
      size: sizeName(m[2]),
    };
    if (m[4]) out.finish = FINISH[m[4]] ? `${FINISH[m[4]]} (${m[4]})` : m[4];
    return out;
  }
  if (itemType === "Cover" || itemType === "CoverBase") {
    // Solstice / Pacifica Marine: C908-F04, C118-SA04-DWV, C908-FD10
    let m = sku.match(/^C(\d{3,4})[A-Z]*-(FD\d+|F\d+|SA\d+)(?:-(SWV|DWV))?$/);
    if (m) {
      const out: Record<string, string> = {
        line: m[2].startsWith("SA") ? "Pacifica Marine" : "Solstice",
        colour: FABRIC[m[2]] ?? m[2],
        size: sizeName(m[1]),
      };
      if (m[3]) out.vent = m[3] === "SWV" ? "Single wind vent" : "Double wind vent";
      return out;
    }
    // Sunbrella pooled pattern: C908-5401 (size + fabric)
    m = sku.match(/^C(\d{3,4})[A-Z]*-(\d{4,5})$/);
    if (m) {
      return {
        line: "Sunbrella",
        colour: SUNBRELLA[m[2]] ? `Sunbrella ${m[2]} ${SUNBRELLA[m[2]]}` : `Sunbrella ${m[2]}`,
        size: sizeName(m[1]),
      };
    }
    return {};
  }
  if (itemType === "Base") {
    const m = sku.match(/^([A-Z]+(?:-[A-Z]+)?)/);
    return m ? { family: m[1] } : {};
  }
  if (itemType === "Fabric") {
    return { fabric: SUNBRELLA[sku] ? `${sku} ${SUNBRELLA[sku]}` : sku };
  }
  if (itemType === "Trim") {
    if (/^BD/.test(sku)) return { kind: "Binding", colour: sku.slice(2) };
    if (/^(FRINGE|FT-FRN)/.test(sku)) return { kind: "Fringe", colour: sku.replace(/^(FRINGE-?|FT-FRN\d*-)/, "") || "Standard" };
    if (/^(PT-TASSEL|FT-TAS)/.test(sku)) return { kind: "Tassel", colour: sku.replace(/^(PT-TASSEL-|FT-TAS\d*-)/, "") };
    return { kind: "Other" };
  }
  return { part: sku };
}
